import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RolePermission {
  id: string;
  role: 'member' | 'admin' | 'super_admin';
  permission: string;
  description?: string;
}

interface UserPermissionOverride {
  id: string;
  user_id: string;
  permission: string;
  granted: boolean;
  granted_by?: string;
  granted_at: string;
  reason?: string;
}

export const usePermissions = () => {
  const { user, permissions, hasPermission: checkPermission, role, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // 🔥 OPTIMISATION: Permissions déjà chargées dans useAuth
  // Plus besoin de requêtes séparées pour role_permissions et user_roles

  // Get permission overrides for all users (admin only)
  const { data: permissionOverrides, isLoading: isLoadingOverrides } = useQuery({
    queryKey: ['permission-overrides'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_permission_overrides')
        .select('*');
      
      if (error) throw error;
      return data as UserPermissionOverride[];
    },
  });

  // Grant permission override
  const grantPermissionMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      permission, 
      granted, 
      reason 
    }: { 
      userId: string; 
      permission: string; 
      granted: boolean; 
      reason?: string; 
    }) => {
      const { error } = await supabase
        .from('user_permission_overrides')
        .upsert({
          user_id: userId,
          permission,
          granted,
          granted_by: user?.id,
          reason
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });

  // Remove permission override
  const removeOverrideMutation = useMutation({
    mutationFn: async ({ userId, permission }: { userId: string; permission: string }) => {
      const { error } = await supabase
        .from('user_permission_overrides')
        .delete()
        .eq('user_id', userId)
        .eq('permission', permission);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });

  // 🔥 OPTIMISATION: Charger role_permissions seulement pour l'admin UI
  const { data: rolePermissions } = useQuery({
    queryKey: ['role-permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*');
      
      if (error) throw error;
      return data as RolePermission[];
    },
    enabled: role === 'admin' || role === 'super_admin', // Seulement pour admins
  });

  // Get permissions by role with hierarchy (pour l'UI admin)
  const getPermissionsByRole = (targetRole: 'member' | 'admin' | 'super_admin'): string[] => {
    if (!rolePermissions) return [];
    
    let perms: string[] = [];
    
    // Add member permissions for all roles
    perms = perms.concat(
      rolePermissions
        .filter(rp => rp.role === 'member')
        .map(rp => rp.permission)
    );
    
    // Add admin permissions for admin and super_admin
    if (targetRole === 'admin' || targetRole === 'super_admin') {
      perms = perms.concat(
        rolePermissions
          .filter(rp => rp.role === 'admin')
          .map(rp => rp.permission)
      );
    }
    
    // Add super_admin permissions for super_admin only
    if (targetRole === 'super_admin') {
      perms = perms.concat(
        rolePermissions
          .filter(rp => rp.role === 'super_admin')
          .map(rp => rp.permission)
      );
    }
    
    return [...new Set(perms)]; // Remove duplicates
  };

  return {
    rolePermissions,
    userPermissions: permissions, // 🔥 Depuis useAuth
    permissionOverrides,
    isLoading: authLoading || isLoadingOverrides,
    hasPermission: checkPermission, // 🔥 Depuis useAuth
    getPermissionsByRole,
    grantPermission: grantPermissionMutation.mutate,
    removeOverride: removeOverrideMutation.mutate,
    isGrantingPermission: grantPermissionMutation.isPending,
    isRemovingOverride: removeOverrideMutation.isPending,
  };
};